import { Autocomplete, FormControlLabel, Grid, Radio, RadioGroup, TextField, Typography } from '@mui/material';

import { find, get, map, size } from 'lodash';
import React from 'react';
import { getFormattedCell } from 'utils/formatBrackets';
import { SamplingParams } from './types';

export const SamplingParametersStep = ({
  samplingParams,
  setSamplingParams,
  errors,
  cellClasses,
}: {
  samplingParams: SamplingParams;
  setSamplingParams: (params: SamplingParams) => void;
  errors: any;
  cellClasses: string[];
}) => {
  const handleSamplingParamsChange = (newValues: { [key: string]: any }) => {
    setSamplingParams({
      ...samplingParams,
      ...(newValues || {}),
    });
  };

  const cellClassesToExcludeOptions = map(cellClasses, (cellClass) => ({
    label: getFormattedCell(cellClass),
    value: cellClass,
  }));

  return (
    <Grid container direction="column" spacing={2} mt={1}>
      <Grid item container alignItems="center" spacing={1}>
        <Grid item xs={6}>
          <TextField
            label="Max. No. Cells Per Slide"
            size="small"
            value={samplingParams.absoluteMaxCellsPerSlide}
            type="number"
            onChange={(event) => handleSamplingParamsChange({ [event.target.name]: Number(event.target.value) })}
            name="absoluteMaxCellsPerSlide"
            inputProps={{
              step: 1,
              min: 1,
              type: 'number',
            }}
            error={Boolean(get(errors, 'absoluteMaxCellsPerSlide'))}
            helperText={
              get(errors, 'absoluteMaxCellsPerSlide')?.message ||
              `Maximum number of cells to include from each slide (after exclusions). If set with ‘Max Cell Fraction Per Slide’, the smaller limit is used`
            }
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Max Cell Fraction Per Slide"
            size="small"
            value={samplingParams.maxCellsFractionPerSlide}
            type="number"
            onChange={(event) => handleSamplingParamsChange({ [event.target.name]: Number(event.target.value) })}
            name="maxCellsFractionPerSlide"
            inputProps={{
              step: 0.01,
              min: 0,
              max: 1,
              type: 'number',
            }}
            error={Boolean(get(errors, 'maxCellsFractionPerSlide'))}
            helperText={
              get(errors, 'maxCellsFractionPerSlide')?.message ||
              `Sample at most this fraction of available cells per slide (after exclusions). If set with ‘Absolute Max. Cells Per Slide’, the smaller limit is used`
            }
          />
        </Grid>
      </Grid>
      <Grid item>
        <Autocomplete
          multiple
          options={cellClassesToExcludeOptions}
          value={
            samplingParams.cellClassesToExclude
              ? map(
                  samplingParams.cellClassesToExclude,
                  (cellClass) => find(cellClassesToExcludeOptions, { value: cellClass })!
                )
              : []
          }
          onChange={(event, newValue) =>
            handleSamplingParamsChange({ cellClassesToExclude: size(newValue) > 0 ? map(newValue, 'value') : null })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Cell Classes To Exclude"
              size="small"
              error={Boolean(get(errors, 'cellClassesToExclude'))}
              helperText={
                get(errors, 'cellClassesToExclude')?.message ||
                `Excluded classes do not contribute to the total cell count and will not be sampled`
              }
            />
          )}
        />
      </Grid>
      <Grid item>
        <RadioGroup
          value={samplingParams.weightedSamplingOfCellClasses ? 'weighted' : 'balanced'}
          onChange={(event) => {
            const value = event.target.value;
            if (value === 'weighted') {
              handleSamplingParamsChange({ weightedSamplingOfCellClasses: true, balancedSamplingOfCellClasses: false });
            } else {
              handleSamplingParamsChange({ weightedSamplingOfCellClasses: false, balancedSamplingOfCellClasses: true });
            }
          }}
        >
          <Grid container direction="row" alignItems="center">
            <Grid item xs={6}>
              <FormControlLabel value="weighted" control={<Radio />} label="Weighted Sampling of Cell Classes" />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mb: 1 }}>
                Preserve the slide&apos;s original cell-class proportions in the sample. The total number of sampled
                cells is split across classes in proportion to their abundance.
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel value="balanced" control={<Radio />} label="Balanced Sampling of Cell Classes" />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4, mb: 1 }}>
                Oversample rare classes by sampling approximately 1/f cells per class, where f is the class fraction
                within the slide (after exclusions).
              </Typography>
            </Grid>
          </Grid>
        </RadioGroup>
      </Grid>
    </Grid>
  );
};
